import React from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { mobiliTheme } from "../themes/mobiliTheme";
import { useTherapyColorMap } from "../hooks/useTherapyColorMap";
import type { TherapyColorKey } from "../helpers/slotTherapy";
import { Avatar, Badge, Box, Tooltip } from "@mui/material";
import { getVisitTypeIcon } from "../helpers/visitTypeMapping";
import { Euro, MedicalServices, Person, Sell } from "@mui/icons-material";
import { SublocalityDto } from "../api-client";

const GenericChip = ({ label, onDelete, sx, ...props }: any) => {
  return (
    <Chip
      label={label}
      onDelete={onDelete}
      sx={
        sx
          ? sx
          : {
              width: "100%",
              wordWrap: "break-word",
              border: "3px solid blue", // Add a border for debugging
              padding: "0px",
            }
      }
      {...props}
    />
  );
};


export const SmallChip = React.forwardRef<any, any>(({  sx, ...props }, ref) => {
  let bgColor: string = mobiliTheme.topLevelBackground;
  const defaultSx = {
    width: "100%",
    wordWrap: "break-word",
    border: "0px solid blue", // Add a border for debugging
    padding: "0px",
    fontSize: "0.6em",
    borderRadius: ".9em",
    backgroundColor: bgColor,
    "& .MuiChip-label": {
      // Targeting the label specifically
      padding: "6px", // Adjust the padding as needed
    },
  }

  return (
      <Chip
        ref={ref}
        sx={
          sx
            ? { ...defaultSx, ...sx }
            : defaultSx
        }
        {...props}
      />
  );
});

SmallChip.displayName = "SmallChip";


interface TherapyChipProps {
  /** Display text — an abbreviation, shortLabel or label. Never a lookup key. */
  therapyName: string;
  /**
   * MD-232: the catalog IDENTITY to colour by — a TherapyMethod id for a line
   * item, a PrescribedTherapy id for a term. Required (though it may be
   * `undefined`) so that adding a chip forces the question "what identity is
   * this?" instead of quietly reusing `therapyName`. Colouring by display text
   * is what made renamed Heilmittel go grey (D-RX-05); `undefined` renders the
   * neutral fallback, which is the honest answer when no identity is at hand.
   */
  colorKey: TherapyColorKey | undefined;
  amount?: number | string | null;
  sx?: any;
  [key: string]: any;
}

export const TherapyChip = ({ therapyName, colorKey, amount, sx, ...props }: TherapyChipProps) => {
  // Catalog-sourced colour map (24h-cached reference data), theme fallback.
  const colorMap = useTherapyColorMap();

  let bgColor: string = (colorKey && colorMap[colorKey]) || "grey";

  const defaultSx = {
    backgroundColor: bgColor,
  }

  return (
    <Tooltip title={therapyName}>
      <Box>
      <SmallChip
        label={amount ? `${amount} ${therapyName}` : therapyName}
        sx={
          sx
            ? { ...defaultSx, ...sx }
            : defaultSx
        }
        {...props}
      /></Box></Tooltip>
   
  );
};

interface VisitTypeBadgeProps {
  visitType: string;
  visitFee?: string | null;
  sx?:any;
  sublocality?: SublocalityDto;
}

export const VisitTypeBadge: React.FC<VisitTypeBadgeProps> = ({ visitType, visitFee ,sx, sublocality }) => {
  const color = sx?.color || sublocality?.color || mobiliTheme.mainHeader;
  const icon = getVisitTypeIcon(visitType, color) || null;
  const hasFee = Boolean(visitFee && visitFee !== "keine");

  return (
    visitType && (
      <Tooltip
        title={
          <>
            Visit Type: {visitType}
            {sublocality?.name && (
              <>
                <br />
                Stadtteile: {sublocality.name}
              </>
            )}
            {hasFee && (
              <>
                <br />
                Visit Fee: {visitFee}
              </>
            )}
          </>
        }
      >
         <Badge
            badgeContent={hasFee ? <Sell sx={{ color, display: "block" }} /> : null}
            color="primary"
            overlap="circular"
            sx={{
              backgroundColor: "transparent",
              "& .MuiBadge-badge": {
                backgroundColor: "transparent",
                color: sublocality?.color,
                borderRadius: "90%",
                // Hug the glyph. The old 20x14 box with 2px padding was wider
                // and taller than the icon underneath it, and `overlap` threw
                // that surplus up and to the right — far enough, on a dense
                // row, to sit on top of the time in the line above.
                minWidth: 0,
                width: "auto",
                height: "auto",
                padding: 0,
                // Kept nearly level with the icon's top edge: dense rows have no
                // vertical padding, so a badge thrown further up crosses into
                // the date/time line above and sits on top of it.
                transform: "scale(1) translate(40%, -10%)",
              },
              /*
               * The tag is sized from HERE, not from the Sell element's own sx.
               * Dense rows style their icons with a parent rule
               * (`& .MuiSvgIcon-root { font-size: var(--row-icon) }`), and that
               * descendant selector also matches this glyph and outranks a
               * plain `sx` on the element — so the tag was being blown up to
               * the full size of the icon it is supposed to annotate. Matching
               * one level deeper wins it back.
               *
               * Sized off the icon rather than fixed, so the tag keeps its
               * proportion as the icons shrink (16px here, 14px under
               * `data-density="compact"`).
               */
              "& .MuiBadge-badge .MuiSvgIcon-root": {
                fontSize: "calc(var(--row-icon, 16px) * 0.6)",
              },
              ...sx,
            }}
          >
            {icon}
          </Badge>
      </Tooltip>
    )
  );
  
};

export interface GapBadgeProps {
  gapInDays?: number | null;
  gapColor?: string;
  sx?: any;
}

export const GapBadge: React.FC<GapBadgeProps> = ({ gapInDays, gapColor, sx }) => {
  if (!gapColor) return null;

  const displayNumber = gapInDays !== null && gapInDays !== undefined ? gapInDays : "-";

  return (
    <Tooltip title={`Gap: ${displayNumber} Tage`}>
      <Box sx={{ display: "inline-flex", ...sx }}>
        <Avatar
          sx={{
            width: 30,
            height: 30,
            backgroundColor: "white",
            color: gapColor,
            fontWeight: 600,
            border: `3px solid ${gapColor}`, // thicker border showing the color
            fontSize: 16,
          }}
        >
          {displayNumber}
        </Avatar>
      </Box>
    </Tooltip>
  );
};

export const BillChip = ({ amount, isCopayment, sx, ...props }: any) => {
  // Define the color based on isCopayment value
  const chipColor = isCopayment ? mobiliTheme.palette.error.main : mobiliTheme.palette.info.main;

  return (
    <Tooltip title={amount}>
       <Chip label={<Euro sx={{ color: chipColor  }} />} />    
    </Tooltip>
  );
};

export interface CancellationOriginChipProps {
  /** True when the cancellation was initiated by the therapist/practice. */
  byTherapist?: boolean | null;
  sx?: any;
}

/**
 * Chip that makes it unambiguous whether a cancellation came from the therapist
 * (practice) side or the patient side. The underlying `byTherapist` flag alone
 * is easy to misread (e.g. "Urlaub" could be either party), so this surfaces it
 * explicitly with a label, icon and colour.
 */
export const CancellationOriginChip: React.FC<CancellationOriginChipProps> = ({
  byTherapist,
  sx,
}) => {
  const isTherapist = Boolean(byTherapist);
  const label = isTherapist ? "vom Therapeuten" : "vom Patienten";
  const color = isTherapist ? mobiliTheme.palette.info.main : mobiliTheme.palette.secondary.main;
  const icon = isTherapist ? (
    <MedicalServices sx={{ fontSize: "0.95em", color }} />
  ) : (
    <Person sx={{ fontSize: "0.95em", color }} />
  );

  return (
    <Tooltip title={`Storniert ${label}`}>
      <Chip
        size="small"
        variant="outlined"
        icon={icon}
        label={label}
        sx={{
          height: "auto",
          borderColor: color,
          color,
          fontWeight: 600,
          "& .MuiChip-label": { padding: "2px 8px" },
          "& .MuiChip-icon": { color },
          ...sx,
        }}
      />
    </Tooltip>
  );
};
// const GenericChipList = ({ chips }:any) => {
//   return (
//     <Stack direction="row" spacing={1}>
//       {chips.map((chip:any, index:any) => (
//         <GenericChip
//           key={index}

//           label={chip.label}
//           onDelete={chip.onDelete ? () => chip.onDelete(index) : undefined}
//           {...chip.props}
//         />
//       ))}
//     </Stack>
//   );
// };

export default GenericChip;
